/**
 * "Copy AI prompt" button: drops a ready-to-paste briefing into the clipboard so
 * vibe coders can hand it to their AI assistant and wire Fast2D into a game
 * without reading the docs first. Kept dependency-free and tiny.
 */

const PROMPT = `You are helping me add 2D physics to my game using Fast2D, a tiny dependency-free TypeScript physics engine (npm package: "fast-2d").

Install it:
  npm install fast-2d

Core API:
- import { World, Bodies } from "fast-2d";
- const world = new World({ gravity: [0, 9.81] }); // +y points DOWN
- world.add(Bodies.circle({ position: [x, y], radius })) for dynamic circles
- world.add(Bodies.rectangle({ position: [x, y], width, height })) for boxes
- pass { isStatic: true } for immovable ground/walls
- Factories accept Vec2, [x, y] tuples, or { x, y }
- Step the simulation with a FIXED timestep every frame:
    world.update(deltaSeconds); // built-in accumulator, returns steps taken
  or world.step(1 / 60) for a single fixed step.
- Read back each body's body.position (x, y) and body.angle to draw it. The
  engine is renderer-agnostic, so I do the drawing (Canvas2D / WebGL / my engine).

Please:
1. Set up a World with sensible gravity for my game.
2. Create the bodies my game needs (static ground + dynamic objects).
3. Add a fixed-timestep update loop and map each body's position/angle onto my
   existing render/sprite code.
4. Show the minimal integration code for my current stack.

My game stack / what I have so far: <DESCRIBE YOUR SETUP HERE>`;

export function initCopyPrompt(): void {
	const btn = document.getElementById("btn-copy-prompt") as HTMLButtonElement | null;
	if (!btn) return;

	const reset = btn.dataset.default ?? btn.textContent ?? "Copy AI prompt";
	let timer: number | undefined;

	btn.addEventListener("click", async () => {
		try {
			await navigator.clipboard.writeText(PROMPT);
			btn.textContent = "Prompt copied — paste it into your AI ✓";
		} catch {
			btn.textContent = "Press Ctrl+C to copy";
		}
		window.clearTimeout(timer);
		timer = window.setTimeout(() => {
			btn.textContent = reset;
		}, 2400);
	});
}
